// src/hooks/smsReplyStore.js
//
// Singleton subscription layer for unified SMS replies
// (features/unified-replies/03-architecture.md, decision A-1).
//
// Owns every Firestore listener the feature needs, exactly once, no
// matter how many badge surfaces mount useInboxUnreadCount or how many
// pages mount useSmsReplyUnread. Consumers attach through
// subscribeSmsReplyStore/getSmsReplySnapshot (useSyncExternalStore
// contract).
//
// Data flow:
//   users/{uid}/teams (mirror, realtime)  ─┐
//   legacy membership discovery (one-time) ─┴→ staff team set (≤30)
//        → one inboundSms where('read','==',false) listener per team
//        → reduce to {count, newestAtMs}   ← SECURITY BOUNDARY (S2):
//          doc data is dropped inside the snapshot callback; nothing
//          past this point holds phone/parentName/parentEmail/body/
//          translations. Log hygiene per S3: teamId + error code only.

import {
  collection, collectionGroup, doc, getDoc, getDocs,
  onSnapshot, query, where,
} from 'firebase/firestore';
import { db } from '@/firebase/firebase';
import {
  MAX_SMS_TEAMS,
  buildPublished,
  makeSettleTracker,
  reduceUnread,
  selectStaffTeams,
  toMillis,
} from './smsReplyHelpers';

const EMPTY_SNAPSHOT = Object.freeze({ units: 0, perTeam: [], settled: true });

// ---- module state ---------------------------------------------------

let currentUid = null;
let subscribers = new Set();
let mirrorUnsub = null;
let teamUnsubs = new Map();        // teamId -> unsubscribe fn
let perTeam = new Map();           // teamId -> {count, newestAtMs}
let names = new Map();             // teamId -> string (session cache)
let nameFetchesInFlight = new Set();
let discoveredIds = new Set();
let discoveryDone = false;
let mirrorEntries = [];
let settleTracker = null;
let mirrorFired = false;
let cappedWarned = false;
let snapshot = EMPTY_SNAPSHOT;     // stable reference for getSnapshot()

// ---- publishing -----------------------------------------------------

function isSettled() {
  return mirrorFired && discoveryDone && (settleTracker ? settleTracker.settled : true);
}

function publish() {
  const settled = isSettled();
  if (!settled && snapshot === EMPTY_SNAPSHOT) {
    // Pre-settle we stay on the empty snapshot (arch A-4): badge shows
    // chat-only counts until every initial listener has reported.
    return;
  }
  const { units, perTeam: rows } = buildPublished(perTeam, names);
  snapshot = { units, perTeam: rows, settled };
  subscribers.forEach((cb) => { try { cb(); } catch { /* consumer's problem */ } });
}

// ---- name resolution (arch A-5) -------------------------------------

function ensureName(teamId) {
  if (names.has(teamId) || nameFetchesInFlight.has(teamId)) return;
  const mirrorName = mirrorEntries.find((e) => e.teamId === teamId)?.name;
  if (mirrorName) {
    names.set(teamId, mirrorName);
    return;
  }
  nameFetchesInFlight.add(teamId);
  getDoc(doc(db, 'teams', teamId))
    .then((snap) => {
      const name = snap.exists() ? String(snap.data()?.name || '').trim() : '';
      if (name) {
        names.set(teamId, name);
        publish();
      }
      // No name → buildPublished skips the row (QA T-11); nothing to do.
    })
    .catch(() => { /* unreadable team doc → row skipped, silently */ })
    .finally(() => { nameFetchesInFlight.delete(teamId); });
}

// ---- per-team listeners ---------------------------------------------

function attachTeam(teamId, initial) {
  if (teamUnsubs.has(teamId)) return;
  const q = query(collection(db, 'teams', teamId, 'inboundSms'), where('read', '==', false));
  const unsub = onSnapshot(
    q,
    (snap) => {
      // SECURITY BOUNDARY (sec review S2): extract receivedAt only and
      // drop every doc reference before this callback returns. Do not
      // add fields here without a security re-review.
      const receivedList = snap.docs.map((d) => toMillis(d.get('receivedAt')));
      perTeam.set(teamId, reduceUnread(receivedList));
      if (perTeam.get(teamId).count > 0) ensureName(teamId);
      if (initial && settleTracker) settleTracker.markFired(teamId);
      publish();
    },
    (err) => {
      // Remove-and-forget (arch A-9). permission-denied terminates the
      // listener on its own; either way this team is out for the session.
      // S3 log hygiene: teamId + code only.
      console.warn('[smsReplyStore] listener dropped', { teamId, code: err?.code });
      teamUnsubs.delete(teamId);
      perTeam.delete(teamId);
      if (initial && settleTracker) settleTracker.markFired(teamId);
      publish();
    },
  );
  teamUnsubs.set(teamId, unsub);
}

function syncTeamListeners({ initial = false } = {}) {
  const { teams, dropped } = selectStaffTeams(mirrorEntries, discoveredIds, MAX_SMS_TEAMS);
  if (dropped > 0 && !cappedWarned) {
    cappedWarned = true;
    console.warn('[smsReplyStore] staff team cap reached', { cap: MAX_SMS_TEAMS, dropped });
  }

  const wanted = new Set(teams.map((t) => t.teamId));
  for (const [teamId, unsub] of teamUnsubs.entries()) {
    if (!wanted.has(teamId)) {
      unsub();
      teamUnsubs.delete(teamId);
      perTeam.delete(teamId);
    }
  }
  if (initial) settleTracker = makeSettleTracker(Array.from(wanted));
  for (const t of teams) attachTeam(t.teamId, initial);
  if (wanted.size === 0) publish(); // nothing to wait for → settle now
}

// ---- membership sources ---------------------------------------------

function readMirrorSnapshot(snap) {
  mirrorEntries = snap.docs.map((d) => {
    const data = d.data() || {};
    return {
      teamId: d.id,
      role: data.role,
      name: String(data.name || '').trim() || null,
      joinedAtMs: toMillis(data.joinedAt),
    };
  });
}

// One-time legacy discovery (arch A-2). Every query is individually
// swallowed, exactly like useMyTeams: the collectionGroup('members')
// query in particular is EXPECTED to be permission-denied under current
// rules (sec review S4 — do not "fix" by adding a collection-group
// rule). The public-team-field queries carry the load.
async function discoverLegacyTeamIds(uid) {
  const ids = new Set();
  try {
    const cg = await getDocs(query(collectionGroup(db, 'members'), where('uid', '==', uid)));
    cg.docs.forEach((d) => {
      const teamId = d.ref?.parent?.parent?.id;
      if (teamId) ids.add(teamId);
    });
  } catch { /* expected under current rules — see S4 */ }
  const legacyQueries = [
    query(collection(db, 'teams'), where('staffIds', 'array-contains', uid)),
    query(collection(db, 'teams'), where('coachId', '==', uid)),
  ];
  for (const q2 of legacyQueries) {
    try {
      (await getDocs(q2)).docs.forEach((d) => ids.add(d.id));
    } catch { /* best-effort */ }
  }
  return ids;
}

// ---- lifecycle ------------------------------------------------------

function teardown() {
  mirrorUnsub?.();
  mirrorUnsub = null;
  teamUnsubs.forEach((u) => u());
  teamUnsubs = new Map();
  perTeam = new Map();
  names = new Map();
  nameFetchesInFlight = new Set();
  discoveredIds = new Set();
  discoveryDone = false;
  mirrorEntries = [];
  settleTracker = null;
  mirrorFired = false;
  cappedWarned = false;
  snapshot = EMPTY_SNAPSHOT;
}

function start(uid) {
  currentUid = uid;
  if (!uid) return; // signed out: stay on EMPTY_SNAPSHOT (settled: true)

  let firstMirror = true;
  mirrorUnsub = onSnapshot(
    collection(db, 'users', uid, 'teams'),
    (snap) => {
      readMirrorSnapshot(snap);
      mirrorFired = true;
      syncTeamListeners({ initial: firstMirror });
      firstMirror = false;
    },
    (err) => {
      // Mirror unreadable → no SMS units this session; chat badge is
      // unaffected. S3 hygiene: code only.
      console.warn('[smsReplyStore] mirror listener failed', { code: err?.code });
      mirrorFired = true;
      discoveryDone = true;
      publish();
    },
  );

  discoverLegacyTeamIds(uid)
    .then((ids) => { discoveredIds = ids; })
    .finally(() => {
      discoveryDone = true;
      if (mirrorFired) syncTeamListeners({ initial: false });
      publish();
    });
}

// ---- public API -----------------------------------------------------

/**
 * useSyncExternalStore subscribe function. Keyed by uid: the first
 * subscriber (or a uid change) boots the listeners; the last unsubscribe
 * tears everything down.
 */
export function subscribeSmsReplyStore(uid, callback) {
  if (uid !== currentUid) {
    teardown();
    start(uid || null);
  }
  subscribers.add(callback);
  return () => {
    subscribers.delete(callback);
    if (subscribers.size === 0) {
      teardown();
      currentUid = null;
    }
  };
}

/** useSyncExternalStore getSnapshot function. Stable reference between publishes. */
export function getSmsReplySnapshot() {
  return snapshot;
}
